import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { RefreshCw, ChevronRight, Plus, Pencil, Trash2, Archive, RotateCcw } from "lucide-react";

export default function Stations() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stations, setStations] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ code: "", name: "", location: "", lat: "", lng: "", user_ids: [] });
  const [users, setUsers] = useState([]);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [retentionInput, setRetentionInput] = useState("30");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/stations");
    setStations(data);
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.role === "user"))).catch(() => {});
    }
  }, [isAdmin]);

  const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : t("stations.noSignal"));
  const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString() : "—");
  const toggleUserId = (id) => setCreateForm((f) => ({
    ...f, user_ids: f.user_ids.includes(id) ? f.user_ids.filter((x) => x !== id) : [...f.user_ids, id],
  }));

  const createStation = async () => {
    if (!createForm.code.trim()) { toast.error(t("stations.code")); return; }
    setBusy(true);
    try {
      await api.post("/stations", {
        code: createForm.code.trim(),
        name: createForm.name.trim() || null,
        location: createForm.location.trim() || null,
        lat: createForm.lat === "" ? null : Number(createForm.lat),
        lng: createForm.lng === "" ? null : Number(createForm.lng),
        user_ids: createForm.user_ids,
      });
      toast.success(t("stations.created"));
      setShowCreate(false);
      setCreateForm({ code: "", name: "", location: "", lat: "", lng: "", user_ids: [] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  const renameStation = async () => {
    if (!renameValue.trim()) return;
    setBusy(true);
    try {
      await api.put(`/stations/${renameTarget.code}`, { name: renameValue.trim() });
      toast.success(t("stations.renamed"));
      setRenameTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/stations/${deleteTarget.code}`);
      toast.success(data.archived ? t("stations.stationArchived") : t("stations.deleted"));
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  const openArchived = async () => {
    setShowArchived(true);
    try {
      const { data } = await api.get("/stations/archived");
      setArchived(data.stations || []);
      setRetentionDays(data.retention_days);
      setRetentionInput(String(data.retention_days));
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const saveRetention = async () => {
    const d = parseInt(retentionInput, 10);
    if (Number.isNaN(d) || d < 0) return;
    setBusy(true);
    try {
      await api.put("/stations/retention", { days: d });
      setRetentionDays(d);
      toast.success(t("stations.retentionSaved"));
      openArchived();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  const restoreStation = async (code) => {
    setBusy(true);
    try {
      await api.post(`/stations/${code}/restore`);
      toast.success(t("stations.restored"));
      setArchived((prev) => prev.filter((s) => s.code !== code));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <PageHeader
        title={t("stations.title")}
        subtitle={t("stations.subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <>
                <Button onClick={() => setShowCreate(true)} data-testid="new-station-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                  <Plus className="mr-1.5 h-4 w-4" /> {t("stations.newStation")}
                </Button>
                <Button variant="outline" onClick={openArchived} data-testid="archived-stations-btn">
                  <Archive className="mr-1.5 h-4 w-4" /> {t("stations.archived")}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={load} data-testid="refresh-stations">
              <RefreshCw className="mr-2 h-4 w-4" /> {t("common.refresh")}
            </Button>
          </div>
        }
      />
      <div className="p-6">
        <Card className="border-slate-200 shadow-none">
          <Table data-testid="stations-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("stations.station")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("stations.lastConnection")}</TableHead>
                <TableHead>{t("common.location")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stations.map((s) => (
                <TableRow key={s.id} data-testid={`station-row-${s.code}`}
                  className="cursor-pointer" onClick={() => navigate(`/stations/${s.id}`)}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="font-mono text-xs text-slate-400">{s.code}</div>
                  </TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-sm text-slate-500">{fmt(s.last_seen)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{s.location || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="sm" data-testid={`rename-station-${s.code}`}
                            onClick={(e) => { e.stopPropagation(); setRenameTarget(s); setRenameValue(s.name || ""); }}
                            className="text-slate-500 hover:text-blue-600" title={t("stations.rename")}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" data-testid={`delete-station-${s.code}`}
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                            className="text-slate-500 hover:text-red-600" title={t("stations.delete")}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" data-testid={`open-station-${s.code}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/stations/${s.id}`); }}
                        className="text-blue-600 hover:text-blue-700">
                        {t("common.viewDetail")} <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {stations.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-400">
                  {t("common.noData")}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create station */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-station-dialog">
          <DialogHeader>
            <DialogTitle>{t("stations.createTitle")}</DialogTitle>
            <DialogDescription>{t("stations.createDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">{t("stations.code")}</Label>
              <Input value={createForm.code} onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                placeholder="EST001" data-testid="create-code" />
              <p className="mt-1 text-xs text-slate-400">{t("stations.codeHint")}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">{t("stations.name")}</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="create-name" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">{t("stations.location")}</Label>
              <Input value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })} data-testid="create-location" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-slate-500">{t("stations.latitude")}</Label>
                <Input type="number" value={createForm.lat} onChange={(e) => setCreateForm({ ...createForm, lat: e.target.value })} data-testid="create-lat" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">{t("stations.longitude")}</Label>
                <Input type="number" value={createForm.lng} onChange={(e) => setCreateForm({ ...createForm, lng: e.target.value })} data-testid="create-lng" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">{t("stations.assignAccess")}</Label>
              <p className="mb-1.5 mt-0.5 text-xs text-slate-400">{t("stations.assignAccessHint")}</p>
              <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="create-access-list">
                {users.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">{t("stations.noUsers")}</p>}
                {users.map((u) => (
                  <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-50" data-testid={`create-access-${u.id}`}>
                    <input type="checkbox" checked={createForm.user_ids.includes(u.id)} onChange={() => toggleUserId(u.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                    <span className="text-slate-700">{u.name}</span>
                    <span className="font-mono text-xs text-slate-400">{u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("stations.cancel")}</Button>
            <Button onClick={createStation} disabled={busy} data-testid="create-station-submit" className="bg-blue-600 text-white hover:bg-blue-700">
              {t("stations.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename station */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="rename-station-dialog">
          <DialogHeader>
            <DialogTitle>{t("stations.renameTitle")}</DialogTitle>
            <DialogDescription>{t("stations.renameDesc")}</DialogDescription>
          </DialogHeader>
          {renameTarget && (
            <div className="space-y-3">
              <p className="font-mono text-xs text-slate-400">{t("stations.code")}: {renameTarget.code}</p>
              <div>
                <Label className="text-xs uppercase text-slate-500">{t("stations.name")}</Label>
                <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid="rename-input" autoFocus />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>{t("stations.cancel")}</Button>
            <Button onClick={renameStation} disabled={busy} data-testid="rename-submit" className="bg-blue-600 text-white hover:bg-blue-700">
              {t("stations.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / archive confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-station-dialog">
          <DialogHeader>
            <DialogTitle>{t("stations.deleteTitle")}</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>
                  <span className="font-medium text-slate-700">{deleteTarget.name}</span>
                  <span className="ml-1 font-mono text-xs text-slate-400">({deleteTarget.code})</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className={`rounded-md border p-3 text-sm ${deleteTarget.has_data ? "border-amber-200 bg-amber-50 text-amber-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="delete-note">
              {deleteTarget.has_data ? t("stations.deleteArchiveNote") : t("stations.deleteHardNote")}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t("stations.cancel")}</Button>
            <Button onClick={confirmDelete} disabled={busy} data-testid="delete-station-confirm"
              className={deleteTarget?.has_data ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-red-600 text-white hover:bg-red-700"}>
              {deleteTarget?.has_data ? t("stations.archived") : t("stations.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived stations */}
      <Dialog open={showArchived} onOpenChange={setShowArchived}>
        <DialogContent className="max-w-2xl" data-testid="archived-stations-dialog">
          <DialogHeader>
            <DialogTitle>{t("stations.archivedTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("stations.archivedTitle")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs uppercase text-slate-500">{t("stations.retentionLabel")}</Label>
                <Input type="number" min="0" value={retentionInput} onChange={(e) => setRetentionInput(e.target.value)}
                  className="mt-1 w-28" data-testid="retention-input" />
              </div>
              <Button variant="outline" size="sm" onClick={saveRetention} disabled={busy} data-testid="retention-save">
                {t("stations.save")}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-400">{t("stations.retentionNote")}</p>
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {archived.length === 0 && <p className="py-8 text-center text-sm text-slate-400" data-testid="no-archived">{t("stations.archivedEmpty")}</p>}
            {archived.map((s) => (
              <div key={s.code} className="flex items-center justify-between border-b border-slate-100 py-2.5 last:border-0" data-testid={`archived-row-${s.code}`}>
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    {s.name}
                    {s.created_manually && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{t("stations.manualBadge")}</span>}
                  </div>
                  <div className="font-mono text-xs text-slate-400">{s.code}{s.location ? ` · ${s.location}` : ""}</div>
                  <div className="mt-0.5 text-xs text-slate-400">
                    {t("stations.archivedAt")}: {fmtDate(s.archived_at)}
                    {s.purge_at && <span className="ml-2 text-amber-600">· {t("stations.purgeOn")} {fmtDate(s.purge_at)}</span>}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => restoreStation(s.code)} disabled={busy} data-testid={`restore-station-${s.code}`}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {t("stations.restore")}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
